/**
 * Radiesthesia Canvas component for pendulum calibration
 * Dark mystical theme with glowing neon energy zones
 */
import { useEffect, useRef, useCallback } from 'react';
import type { Language } from '@/types';
import { getPendulumTranslation } from '@/utils';
import { RADIESTHESIA_ZONES, hexToRgba } from '@/components/features/radiesthesiaZones';

interface RadiesthesiaCanvasProps {
  width?: number;
  height?: number;
  isPreview?: boolean;
  className?: string;
  language?: Language;
}

interface StarPoint {
  x: number;
  y: number;
  r: number;
  a: number;
}

export function RadiesthesiaCanvas({
  width = 400,
  height = 280,
  isPreview = true,
  className = '',
  language = 'me',
}: RadiesthesiaCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<StarPoint[] | null>(null);

  const t = useCallback(
    (key: Parameters<typeof getPendulumTranslation>[0]) => getPendulumTranslation(key, language),
    [language]
  );

  const resetShadow = (ctx: CanvasRenderingContext2D) => {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  };

  const drawBackground = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      canvasWidth: number,
      canvasHeight: number,
      centerX: number,
      centerY: number
    ) => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      const bgGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, Math.max(canvasWidth, canvasHeight) * 0.8
      );
      bgGradient.addColorStop(0, '#1a1040');
      bgGradient.addColorStop(0.5, '#141028');
      bgGradient.addColorStop(1, '#0d0b1a');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    },
    []
  );

  const drawStarField = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      canvasWidth: number,
      canvasHeight: number,
      preview: boolean
    ) => {
      if (!starsRef.current) {
        const count = preview ? 30 : 60;
        starsRef.current = Array.from({ length: count }, () => ({
          x: Math.random(),
          y: Math.random() * 0.75,
          r: Math.random() * 1.2 + 0.3,
          a: Math.random() * 0.35 + 0.08,
        }));
      }

      for (const star of starsRef.current) {
        ctx.beginPath();
        ctx.arc(
          star.x * canvasWidth,
          star.y * canvasHeight,
          star.r,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = `rgba(255, 255, 255, ${star.a})`;
        ctx.fill();
      }
    },
    []
  );

  const drawZones = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number
    ) => {
      RADIESTHESIA_ZONES.forEach((zone) => {
        const startAngle = Math.PI - (zone.start / 100) * Math.PI;
        const endAngle = Math.PI - (zone.end / 100) * Math.PI;

        const gradient = ctx.createRadialGradient(
          centerX, centerY, radius * 0.3,
          centerX, centerY, radius
        );
        gradient.addColorStop(0, hexToRgba(zone.coreColor, 0.45));
        gradient.addColorStop(1, hexToRgba(zone.coreColor, 0.85));

        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, endAngle, startAngle);
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        // Soft zone boundary
        ctx.save();
        ctx.shadowColor = 'rgba(255, 255, 255, 0.08)';
        ctx.shadowBlur = 4;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.10)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      });

      // Outer aura glow
      ctx.save();
      ctx.shadowColor = 'rgba(107, 76, 230, 0.40)';
      ctx.shadowBlur = 20;
      ctx.strokeStyle = 'rgba(107, 76, 230, 0.18)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius + 2, 0, Math.PI);
      ctx.stroke();
      ctx.restore();
    },
    []
  );

  const drawScaleMarkings = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      preview: boolean
    ) => {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.50)';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      const fontSize = preview ? 9 : 18;
      ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Major marks every 10 units
      for (let i = 0; i <= 100; i += 10) {
        const angle = Math.PI - (i / 100) * Math.PI;
        const lineLength = preview ? 15 : 30;
        const textOffset = preview ? 18 : 35;

        const x1 = centerX + Math.cos(angle) * (radius - lineLength);
        const y1 = centerY + Math.sin(angle) * (radius - lineLength);
        const x2 = centerX + Math.cos(angle) * radius;
        const y2 = centerY + Math.sin(angle) * radius;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.lineWidth = preview ? 1.5 : 3;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.50)';
        ctx.stroke();

        // Draw numbers (no background rect)
        const textX = centerX + Math.cos(angle) * (radius + textOffset);
        const textY = centerY + Math.sin(angle) * (radius + textOffset);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.fillText(i.toString(), textX, textY);
      }

      // Minor marks
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = preview ? 1 : 2;
      for (let i = 5; i < 100; i += 10) {
        const angle = Math.PI - (i / 100) * Math.PI;
        const lineLength = preview ? 8 : 15;

        const x1 = centerX + Math.cos(angle) * (radius - lineLength);
        const y1 = centerY + Math.sin(angle) * (radius - lineLength);
        const x2 = centerX + Math.cos(angle) * radius;
        const y2 = centerY + Math.sin(angle) * radius;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    },
    []
  );

  const drawLabels = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      preview: boolean
    ) => {
      const mainFontSize = preview ? 12 : 24;
      const subFontSize = preview ? 8 : 16;

      // Enable text glow
      ctx.save();
      ctx.shadowColor = 'rgba(255, 255, 255, 0.25)';
      ctx.shadowBlur = 6;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.70)';
      ctx.font = `700 ${mainFontSize}px Poppins, system-ui, sans-serif`;
      ctx.textAlign = 'center';

      // Center top label (NEUTRAL)
      const topY = centerY - radius * 0.65;
      ctx.fillText(t('canvasNeutral'), centerX, topY);

      // Subtitle with dimmer glow
      ctx.shadowColor = 'rgba(255, 255, 255, 0.12)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.font = `400 ${subFontSize}px Inter, system-ui, sans-serif`;
      ctx.fillText(t('canvasNeutralSub'), centerX, topY + (preview ? 12 : 24));

      ctx.restore();
    },
    [t]
  );

  const drawCenterArea = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      preview: boolean
    ) => {
      const innerRadius = radius * 0.25;

      // Dark purple center circle
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, innerRadius
      );
      gradient.addColorStop(0, '#2a1854');
      gradient.addColorStop(0.6, '#1a1040');
      gradient.addColorStop(1, '#3a2070');

      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Purple glow stroke
      ctx.save();
      ctx.shadowColor = 'rgba(107, 76, 230, 0.50)';
      ctx.shadowBlur = 15;
      ctx.strokeStyle = 'rgba(107, 76, 230, 0.50)';
      ctx.lineWidth = preview ? 1.5 : 3;
      ctx.stroke();
      ctx.restore();

      // NO (left) / YES (right) text
      ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
      const fontSize = preview ? 9 : 20;
      ctx.font = `700 ${fontSize}px Poppins, system-ui, sans-serif`;
      ctx.textBaseline = 'middle';

      ctx.textAlign = 'right';
      ctx.fillText(t('canvasNo'), centerX - (preview ? 5 : 12), centerY);
      ctx.textAlign = 'left';
      ctx.fillText(t('canvasYes'), centerX + (preview ? 5 : 12), centerY);

      // Vertical dividing line
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - innerRadius * 0.55);
      ctx.lineTo(centerX, centerY + innerRadius * 0.55);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Glowing center energy point
      const dotRadius = preview ? 3 : 8;
      ctx.save();
      ctx.shadowColor = '#5da3f7';
      ctx.shadowBlur = preview ? 8 : 15;
      ctx.beginPath();
      ctx.arc(centerX, centerY, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#5da3f7';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.30)';
      ctx.lineWidth = preview ? 1 : 2;
      ctx.stroke();
      ctx.restore();

      resetShadow(ctx);
    },
    [t]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Position center lower with enough room for the full semicircle + numbers
    const centerX = canvasWidth / 2;
    const padding = isPreview ? 25 : 40;
    const radius = Math.min(canvasWidth / 2 - padding, canvasHeight - padding * 2);
    const centerY = canvasHeight - padding;

    const scale = isPreview ? 0.8 : 1;
    const adjustedRadius = radius * scale;

    drawBackground(ctx, canvasWidth, canvasHeight, centerX, centerY);
    drawStarField(ctx, canvasWidth, canvasHeight, isPreview);
    drawZones(ctx, centerX, centerY, adjustedRadius);
    drawScaleMarkings(ctx, centerX, centerY, adjustedRadius, isPreview);
    drawLabels(ctx, centerX, centerY, adjustedRadius, isPreview);
    drawCenterArea(ctx, centerX, centerY, adjustedRadius, isPreview);
  }, [isPreview, drawBackground, drawStarField, drawZones, drawScaleMarkings, drawLabels, drawCenterArea]);

  useEffect(() => {
    draw();
  }, [draw, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ backgroundColor: '#0d0b1a' }}
    />
  );
}
